import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import BranchService from "src/Services/API/branch.service";
import CompanyService from "src/Services/API/company.service";
import OnboardingService from "src/Services/API/onboarding.service";
import RoleService from "src/Services/API/role.service";
import ServiceTypeService from "src/Services/API/service.service";
import UserService from "src/Services/API/user.service";
import { can, convertFileToBase64, formatUSPhone, handleError, isValidUSPhone, toE164Phone } from "src/Common/function";
import { getLogoUrl } from "src/Common/common";
import Loader from "src/Components/Common/Loader";
import { setVocabulary, term, vocabularize } from "src/Common/vocab";
import BranchAddModal from "src/Pages/Setting/Branch/addmodal";
import BranchTable from "src/Pages/Setting/Branch/branchtable";
import { STEPS, StepRail } from "./StepRail";
import YourData, { EMPTY_IMPORT } from "./YourData";
import { ROLE_PRESETS, TRADES, VOCAB_KEYS } from "./presets";

// The two roles Makani itself depends on: both are anchors for an all-access
// rule in the policy evaluator, so onboarding never creates or removes them.
const PROTECTED_ROLES = ["Administrator", "Super Admin"];

// Two work types are the same type if they only differ in case or padding —
// "service call" must not become a second "Service Call".
const sameWork = (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase();

// What of a data-step import section survives a reload: the file's parsed rows
// with their per-row results. The importing/dragging flags are moments, not
// data — they must come back false.
const persistableImport = ({ fileName, rows, error }) => ({ fileName, rows, error });

// New-customer onboarding, shown once a tenant's Administrator sets their
// password. Teaches Makani how their company works, one step at a time. The
// steps beyond the ones implemented here are stubs; the rail shows the road
// ahead either way.
function Onboarding() {
  const navigate = useNavigate();
  const currentUser = UserService.getCurrentUser();
  const logoInput = useRef(null);

  const [step, setStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // The primary contact is the administrator: their name, email and phone come
  // from their own account — they just accepted the invitation with them — so
  // those fields start filled in. (Phone is stored E.164, shown formatted.)
  const [companyName, setCompanyName] = useState("");
  const [contactName, setContactName] = useState(currentUser?.name || "");
  const [phone, setPhone] = useState(formatUSPhone(currentUser?.phone || ""));
  const [website, setWebsite] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [errors, setErrors] = useState({});

  // Step 2 — the trade preset, and step 3 — the words it seeded.
  const [trade, setTrade] = useState(null);
  const [tradeOther, setTradeOther] = useState("");
  const [vocab, setVocab] = useState({});

  // Step 4 — the work they dispatch (service types) and the branches they
  // dispatch it from. Both are real records, not wizard answers, so this step
  // reads and writes the same endpoints the Settings pages do.
  const [serviceTypes, setServiceTypes] = useState([]);
  const [selectedWork, setSelectedWork] = useState([]);
  const [otherWork, setOtherWork] = useState("");
  const [branches, setBranches] = useState([]);
  const [workLoaded, setWorkLoaded] = useState(false);
  const [isAddBranch, setIsAddBranch] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);

  // Step 5 — the roles the company will hand out. Roles are records too, so this
  // step reads and writes them the same way the IAM settings page does.
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [otherRole, setOtherRole] = useState("");
  const [rolesLoaded, setRolesLoaded] = useState(false);

  // Step 6 — what the data step imported so far. The imports themselves write
  // real records the moment they run; this is only the running tally, kept in a
  // ref as well so consecutive imports never add onto a stale count.
  const [dataSummary, setDataSummary] = useState(null);
  const dataSummaryRef = useRef(null);
  const [dataBusy, setDataBusy] = useState(false);

  // The data step's upload state lives here, not in the step component: the
  // step unmounts on Continue/Back, and coming back must show the same
  // previews and per-row results, not an empty page.
  const [customerImport, setCustomerImport] = useState(EMPTY_IMPORT);
  const [inviteImport, setInviteImport] = useState(EMPTY_IMPORT);
  const [vendorImport, setVendorImport] = useState(EMPTY_IMPORT);
  const [inviteRoleId, setInviteRoleId] = useState(null);

  // The serialized upload state last written to (or read from) the server, so
  // the persist effect below only saves real changes — not the initial mount,
  // not the hydration, not a drag highlight flicking on and off.
  const lastSavedImports = useRef(
    JSON.stringify({
      customers: persistableImport(EMPTY_IMPORT),
      invites: persistableImport(EMPTY_IMPORT),
      vendors: persistableImport(EMPTY_IMPORT),
      inviteRoleId: null,
    }),
  );

  const email = currentUser?.email || "";

  // Resume where the administrator left off: the step lives on the server, so
  // closing the app or signing in again — even elsewhere — picks up the wizard
  // at the same place. The company record is the source of truth for the
  // profile fields themselves.
  useEffect(() => {
    Promise.all([CompanyService.get(), OnboardingService.get()])
      .then(([company, onboarding]) => {
        if (company.status === 200 && company.data) {
          setCompanyName(company.data.name || "");
          setWebsite(company.data.website || "");
          if (company.data.logo) setLogoPreview(getLogoUrl());
          // Phone is the administrator's own, not the company's — it is
          // pre-filled from the cached user, so it isn't read from here.
        }
        // A tenant that already finished onboarding has nothing to do here.
        // Landing on this page anyway (typed URL, stale link) must not re-open
        // the wizard: one Continue would write a not-completed row and sign-in
        // would start bouncing the administrator back into setup.
        if (onboarding.data?.completed) {
          navigate("/work");
          return;
        }
        const answers = onboarding.data?.answers ?? {};
        if (answers.trade?.key) setTrade(answers.trade.key);
        if (answers.trade?.other) setTradeOther(answers.trade.other);
        if (answers.vocab) setVocab(answers.vocab);
        if (answers.data) {
          dataSummaryRef.current = answers.data;
          setDataSummary(answers.data);
        }
        // The data step's uploads come back exactly as they were left — parsed
        // rows, per-row results, chosen role — with the transient flags off.
        if (answers.dataImports) {
          const stored = answers.dataImports;
          const customers = { ...EMPTY_IMPORT, ...(stored.customers || {}) };
          const invites = { ...EMPTY_IMPORT, ...(stored.invites || {}) };
          const vendors = { ...EMPTY_IMPORT, ...(stored.vendors || {}) };
          setCustomerImport(customers);
          setInviteImport(invites);
          setVendorImport(vendors);
          if (stored.inviteRoleId) setInviteRoleId(stored.inviteRoleId);
          lastSavedImports.current = JSON.stringify({
            customers: persistableImport(customers),
            invites: persistableImport(invites),
            vendors: persistableImport(vendors),
            inviteRoleId: stored.inviteRoleId ?? null,
          });
        }

        const saved = onboarding.data?.currentStep ?? 0;
        setStep(Math.min(Math.max(saved, 0), STEPS.length - 1));
      })
      .catch((error) => handleError(error))
      .finally(() => setIsLoading(false));
  }, []);

  // Reload safety net for the data step: the parsed rows and their per-row
  // results are kept in the onboarding answers, debounced and never mid-import
  // (row statuses are still churning then; the finished state saves once).
  useEffect(() => {
    if (customerImport.importing || inviteImport.importing || vendorImport.importing) return;
    const payload = {
      customers: persistableImport(customerImport),
      invites: persistableImport(inviteImport),
      vendors: persistableImport(vendorImport),
      inviteRoleId: inviteRoleId ?? null,
    };
    const serialized = JSON.stringify(payload);
    if (serialized === lastSavedImports.current) return;
    const timer = setTimeout(() => {
      lastSavedImports.current = serialized;
      OnboardingService.save({ answers: { dataImports: payload } }).catch((error) => handleError(error));
    }, 800);
    return () => clearTimeout(timer);
  }, [customerImport, inviteImport, vendorImport, inviteRoleId]);

  const clearError = (field) => setErrors((prev) => ({ ...prev, [field]: undefined }));

  const logoChanged = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast("The logo must be 2MB or smaller");
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  // Picking a trade seeds its vocabulary. Re-picking the trade already selected
  // is a no-op, so it can't wipe words the administrator has since edited.
  const pickTrade = (key) => {
    if (key === trade) return;
    setTrade(key);
    setVocab({ ...TRADES[key].vocab });
    clearError("trade");
    clearError("tradeOther");
  };

  const vocabComplete = VOCAB_KEYS.every(([key]) => (vocab[key] || "").trim());

  // The work step's records are only fetched once the step is reached — the ones
  // before it don't need them. `>=` rather than `===`, because a resumed session
  // can land straight on the review, which has to show them too.
  useEffect(() => {
    if (step < 3 || workLoaded) return;
    setWorkLoaded(true);
    loadBranches();
    // Only the work step itself may seed the preset; arriving anywhere else is
    // not the moment to invent answers.
    loadWorkTypes(step === 3);
  }, [step, workLoaded]);

  const loadBranches = async () => {
    try {
      const response = await BranchService.getAllManaged();
      setBranches(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      handleError(error);
    }
  };

  const loadWorkTypes = async (seedIfEmpty = false) => {
    try {
      const response = await ServiceTypeService.getAll();
      const active = (Array.isArray(response.data) ? response.data : []).filter((type) => !type.isArchived);
      setServiceTypes(active);
      // A company that already has service types is the authority on its own
      // work. Only a company with none is starting fresh, and there the trade
      // preset comes pre-ticked — that is what picking a trade was for.
      setSelectedWork(
        active.length || !seedIfEmpty
          ? active.map((type) => type.serviceType)
          : [...(TRADES[trade]?.workTypes ?? [])],
      );
    } catch (error) {
      handleError(error);
    }
  };

  // The preset's types, plus anything else the company dispatches — types they
  // typed in here, or ones already on their account from a previous visit.
  const presetWork = TRADES[trade]?.workTypes ?? [];
  const customWork = selectedWork.filter((name) => !presetWork.some((preset) => sameWork(preset, name)));

  const toggleWork = (name) => {
    setSelectedWork((prev) =>
      prev.some((picked) => sameWork(picked, name))
        ? prev.filter((picked) => !sameWork(picked, name))
        : [...prev, name],
    );
    clearError("work");
  };

  const addOtherWork = () => {
    const name = otherWork.trim();
    if (!name) return;
    if (selectedWork.some((picked) => sameWork(picked, name))) {
      toast(`"${name}" is already on the list`);
      return;
    }
    setSelectedWork((prev) => [...prev, name]);
    setOtherWork("");
    clearError("work");
  };

  // Reconcile the ticked list with the service types the company actually has:
  // add what's new, archive what was unticked. Archiving rather than deleting,
  // because a service type may already be on a work order.
  //
  // Sequential on purpose — the backend picks each new type's color by counting
  // the existing ones, so saving in parallel would hand out the same color twice.
  const syncServiceTypes = async (picked) => {
    const created = picked.filter((name) => !serviceTypes.some((type) => sameWork(type.serviceType, name)));
    const archived = serviceTypes.filter((type) => !picked.some((name) => sameWork(type.serviceType, name)));

    for (const name of created) {
      await ServiceTypeService.save({ serviceType: name });
    }
    for (const type of archived) {
      await ServiceTypeService.archive(type.id);
    }
    if (created.length || archived.length) await loadWorkTypes();
  };

  const branchAdded = async (data, logoFile) => {
    setIsAddBranch(false);
    try {
      await BranchService.saveBranch(data, logoFile);
      toast("Branch saved successfully");
      await loadBranches();
      clearError("branch");
    } catch (error) {
      handleError(error);
    }
  };

  const branchUpdated = async (branch, logoFile, removeLogo) => {
    setEditingBranch(null);
    try {
      await BranchService.updateBranch(branch, logoFile, removeLogo);
      toast("Branch updated successfully");
      await loadBranches();
    } catch (error) {
      handleError(error);
    }
  };

  const branchDeleted = async (index) => {
    try {
      await BranchService.deleteBranch(branches[index].id);
      toast("Branch deleted successfully");
      await loadBranches();
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    if (step < 4 || rolesLoaded) return;
    setRolesLoaded(true);
    loadRoles(step === 4);
  }, [step, rolesLoaded]);

  // `seedIfEmpty` only on the first visit: afterwards an empty list is an answer
  // ("we only need the Administrator"), and re-seeding the preset would overrule
  // it every time the step is reopened.
  const loadRoles = async (seedIfEmpty = false) => {
    try {
      const response = await RoleService.getAll();
      const all = Array.isArray(response.data) ? response.data : [];
      // The protected roles are shown, but not as something to pick: they exist
      // whatever the company answers here.
      const own = all.filter((role) => !PROTECTED_ROLES.includes(role.name));
      setRoles(all);
      setSelectedRoles(
        own.length || !seedIfEmpty
          ? own.map((role) => role.name)
          : rolePresets.map((preset) => preset.name),
      );
    } catch (error) {
      handleError(error);
    }
  };

  // The field role wears whatever the chosen trade calls it, so an electrical
  // shop hires Electricians. Read from the trade preset rather than the wording
  // answers: `tech` is not one of the words the company is asked for, so it is
  // not saved, and a resumed session would otherwise fall back to "Technician"
  // where a fresh one said "Electrician".
  const rolePresets = ROLE_PRESETS.map((preset) =>
    preset.key === "tech"
      ? { ...preset, name: (TRADES[trade]?.vocab?.tech || "").trim() || preset.name }
      : preset,
  );
  const customRoles = selectedRoles.filter((name) => !rolePresets.some((preset) => sameWork(preset.name, name)));

  const toggleRole = (name) => {
    setSelectedRoles((prev) =>
      prev.some((picked) => sameWork(picked, name))
        ? prev.filter((picked) => !sameWork(picked, name))
        : [...prev, name],
    );
  };

  const addOtherRole = () => {
    const name = otherRole.trim();
    if (!name) return;
    if (PROTECTED_ROLES.some((protectedName) => sameWork(protectedName, name))) {
      toast(`"${name}" already exists`);
      return;
    }
    if (selectedRoles.some((picked) => sameWork(picked, name))) {
      toast(`"${name}" is already on the list`);
      return;
    }
    setSelectedRoles((prev) => [...prev, name]);
    setOtherRole("");
  };

  // Same reconcile as the work types: create what's newly ticked, remove what was
  // unticked. A role that already has people in it can't be removed — the backend
  // refuses, and it is left standing rather than half-deleted.
  const syncRoles = async (picked) => {
    const created = picked.filter((name) => !roles.some((role) => sameWork(role.name, name)));
    const removed = roles.filter(
      (role) =>
        !PROTECTED_ROLES.includes(role.name) && !picked.some((name) => sameWork(role.name, name)),
    );

    for (const name of created) {
      const preset = rolePresets.find((item) => sameWork(item.name, name));
      await RoleService.create({ name, description: preset?.description });
    }
    for (const role of removed) {
      try {
        await RoleService.remove(role.id);
      } catch (error) {
        toast(error?.response?.data?.message || `Couldn't remove the ${role.name} role`);
      }
    }
    if (created.length || removed.length) await loadRoles();
  };

  // Accumulate what the data step imported and persist it right away — the
  // imported records already exist, so the tally must survive a reload even if
  // the administrator never presses Continue.
  const dataImported = (counts) => {
    const prev = dataSummaryRef.current;
    const next = {
      customers: (prev?.customers || 0) + (counts.customers || 0),
      locations: (prev?.locations || 0) + (counts.locations || 0),
      invites: (prev?.invites || 0) + (counts.invites || 0),
      vendors: (prev?.vendors || 0) + (counts.vendors || 0),
    };
    dataSummaryRef.current = next;
    setDataSummary(next);
    OnboardingService.save({ answers: { data: next } }).catch((error) => handleError(error));
  };

  // Continue stays disabled until this step's required fields hold a usable
  // value, so the button never leads to an error the user can't see.
  const canContinue = (() => {
    if (step === 0) {
      return (
        !!companyName.trim() && !!contactName.trim() && (!phone || isValidUSPhone(phone))
      );
    }
    if (step === 1) {
      return !!trade && (trade !== "other" || !!tradeOther.trim());
    }
    if (step === 2) {
      return vocabComplete;
    }
    if (step === 3) {
      // Nothing to dispatch, or nowhere to dispatch it from, and Makani has no
      // work to show them on the other side of this wizard.
      return selectedWork.length > 0 && branches.length > 0;
    }
    if (step === 5) {
      // The data step is optional, but leaving mid-import would hide the
      // per-row results the import is still writing.
      return !dataBusy;
    }
    return true;
  })();

  // Persist the profile, then move on. The company fields and the primary
  // contact's own details (name, phone) live on different records, so they are
  // saved through their own endpoints.
  const saveProfile = async () => {
    const next = {};
    if (!companyName.trim()) next.companyName = "Company name is required";
    if (!contactName.trim()) next.contactName = "Primary contact is required";
    if (phone && !isValidUSPhone(phone)) next.phone = "Enter a valid phone number";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setIsSaving(true);
    try {
      const logoBase64 = logoFile ? await convertFileToBase64(logoFile) : null;
      const name = companyName.trim();
      const contact = contactName.trim();
      const site = website.trim();
      const e164 = phone ? toE164Phone(phone) : "";

      await CompanyService.update({
        data: { name, website: site },
        logoBase64,
      });

      // Name and phone belong to the administrator, not the company. Send only
      // what changed, and keep the cached user in step with the server — the
      // header and this page's own pre-fill both read from it, so a stale cache
      // would revert what was just typed.
      const userChanges = {};
      if (contact !== (currentUser?.name || "")) userChanges.name = contact;
      if (e164 !== (currentUser?.phone || "")) userChanges.phone = e164;
      if (Object.keys(userChanges).length > 0) {
        await UserService.updateUserPartial(currentUser.id, userChanges);
        UserService.saveCurrentUser({ ...currentUser, ...userChanges });
      }
      await advance({ profile: { companyName: name, contactName: contact, email, phone: e164, website: site } });
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Move to the next step and record it, so a later sign-in resumes here.
  // The step is only advanced once the server has it — otherwise a reload would
  // silently drop the user back.
  const advance = async (answers) => {
    const next = Math.min(step + 1, STEPS.length - 1);
    await OnboardingService.save({ currentStep: next, answers });
    setStep(next);
  };

  // Going back (button or rail) is a move too, so it is recorded the same way.
  // Only completed steps are reachable — a later one may need answers not given.
  const goTo = (target) => {
    if (target < 0 || target >= step) return;
    setStep(target);
    OnboardingService.save({ currentStep: target }).catch((error) => handleError(error));
  };

  // What each step contributes to the saved answers, validated first. Returning
  // null aborts the move.
  const answersForStep = () => {
    if (step === 1) {
      const next = {};
      if (!trade) next.trade = "Pick your trade — 'Other' works too";
      else if (trade === "other" && !tradeOther.trim())
        next.tradeOther = "Tell us what your trade is called";
      setErrors(next);
      if (Object.keys(next).length > 0) return null;
      return { trade: { key: trade, other: trade === "other" ? tradeOther.trim() : "" } };
    }
    if (step === 2) {
      const next = {};
      VOCAB_KEYS.forEach(([key, label]) => {
        if (!(vocab[key] || "").trim()) next[`vocab.${key}`] = `${label} is required`;
      });
      setErrors(next);
      if (Object.keys(next).length > 0) return null;
      const trimmed = {};
      VOCAB_KEYS.forEach(([key]) => {
        trimmed[key] = vocab[key].trim();
      });
      return { vocab: trimmed };
    }
    if (step === 3) {
      const next = {};
      if (!selectedWork.length) next.work = "Pick at least one type of work";
      if (!branches.length) next.branch = "Add at least one branch";
      setErrors(next);
      if (Object.keys(next).length > 0) return null;
      return { workSetup: { workTypes: selectedWork.map((name) => name.trim()) } };
    }
    if (step === 4) {
      // No required answer: the Administrator role already exists, so a company
      // that hands out no other role is a company of one, not an unfinished form.
      return { team: { roles: selectedRoles.map((name) => name.trim()) } };
    }
    return {};
  };

  // The last step has nothing left to save — everything it shows was written by
  // the step that collected it. All it does is close the wizard, which is what
  // sign-in checks to decide whether to send them back here.
  const isLast = step === STEPS.length - 1;

  const submit = async () => {
    setIsSaving(true);
    try {
      // The administrator set these branches up but isn't in any of them — they
      // were invited before the company had one. Work is filtered by the branches
      // a user belongs to, so leaving them out means they walk into an empty app.
      if (branches.length) {
        await UserService.assignBranches(
          currentUser.id,
          branches.map((branch) => branch.id),
        );
        // The cached user is what the app reads branches from, so re-read it —
        // otherwise the assignment doesn't show until the next sign-in.
        const me = await UserService.getMe();
        if (me.data) UserService.saveCurrentUser(me.data);
      }
      await OnboardingService.save({ currentStep: step, completed: true });
      toast("You're all set — welcome to Makani");
      navigate("/work");
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const continueClicked = async () => {
    if (isLast) {
      submit();
      return;
    }
    if (step === 0) {
      saveProfile();
      return;
    }
    const answers = answersForStep();
    if (!answers) return;

    setIsSaving(true);
    try {
      // The wording isn't just an onboarding answer — it relabels the app for
      // everyone in the company, so it goes to the company's settings too.
      if (answers.vocab) {
        await CompanyService.saveVocabulary(answers.vocab);
        setVocabulary(answers.vocab);
      }
      // Likewise the work types: they are the company's service types, which
      // every work order is filed under.
      if (answers.workSetup) {
        await syncServiceTypes(answers.workSetup.workTypes);
      }
      // And the roles, which are what the company's next invitations hand out.
      if (answers.team) {
        await syncRoles(answers.team.roles);
      }
      await advance(answers);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const label = (text, required) => (
    <p className="text-[13px] font-OpenSansSemiBold text-titleColor mb-[6px] mt-[14px]">
      {text}
      {required && " *"}
    </p>
  );

  const field = (value, onChange, placeholder, hasError, type = "text", maxLength, onKeyDown) => (
    <div
      className={`w-full h-[38px] border-solid border-[1px] rounded-[8px] flex items-center px-[12px] ${
        hasError ? "border-red-500" : "border-borderColor"
      }`}
    >
      <input
        value={value}
        type={type}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full text-[14px] font-OpenSansRegular text-titleColor bg-transparent outline-none"
      />
    </div>
  );

  const error = (message) =>
    message ? <p className="text-[11px] font-OpenSansRegular text-red-500 mt-1">{message}</p> : null;

  const companyProfile = () => (
    <>
      <p className="text-[24px] font-OpenSansSemiBold text-titleColor">Welcome to Makani 🤙</p>
      <p className="text-[14px] font-OpenSansRegular text-subTitleColor leading-[22px] mt-[6px] mb-[10px]">
        We built Makani inside our own service contracting business. This short setup teaches Makani how <b>your</b>{" "}
        company works — your words, your workflows, your paperwork. You can save and come back anytime.
      </p>

      <div className="flex gap-4">
        <div className="flex-1">
          {label("Company name", true)}
          {field(
            companyName,
            (e) => {
              setCompanyName(e.target.value);
              clearError("companyName");
            },
            "e.g. Volt Electric LLC",
            errors.companyName,
          )}
          {error(errors.companyName)}
        </div>
        <div className="flex-1">
          {label("Primary contact", true)}
          {field(
            contactName,
            (e) => {
              setContactName(e.target.value);
              clearError("contactName");
            },
            "Enter full name",
            errors.contactName,
          )}
          {error(errors.contactName)}
        </div>
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          {label("Email", true)}
          <div className="w-full h-[38px] border-solid border-[1px] border-borderColor rounded-[8px] flex items-center px-[12px] bg-gray-50">
            <span className="text-[14px] font-OpenSansRegular text-subTitleColor truncate">{email}</span>
          </div>
          <p className="text-[12px] font-OpenSansRegular text-subTitleColor mt-1">
            This is the email you sign in with.
          </p>
        </div>
        <div className="flex-1">
          {label("Phone")}
          {field(
            phone,
            (e) => {
              // Formatted as they type, e.g. (555) 123-4567. Stored E.164 on save.
              setPhone(formatUSPhone(e.target.value));
              clearError("phone");
            },
            "(555) 123-4567",
            errors.phone,
            "tel",
            14,
          )}
          {error(errors.phone)}
        </div>
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          {label("Website")}
          {field(website, (e) => setWebsite(e.target.value), "e.g. voltelectric.com", false)}
        </div>
        <div className="flex-1" />
      </div>

      {label("Company logo")}
      <input
        ref={logoInput}
        type="file"
        accept="image/png,image/jpeg"
        onChange={logoChanged}
        className="hidden"
      />
      <div
        onClick={() => logoInput.current?.click()}
        className="w-full border-dashed border-[2px] border-borderColor rounded-[12px] py-[22px] flex flex-col items-center justify-center cursor-pointer hover:border-blueColor"
      >
        {logoPreview ? (
          <img src={logoPreview} alt="Company logo" className="h-[56px] object-contain" />
        ) : (
          <span className="text-[24px]">＋</span>
        )}
        <span className="text-[13px] font-OpenSansRegular text-subTitleColor mt-2">
          {logoPreview ? "Click to replace your logo" : "Click to upload your logo — PNG, JPG or SVG (max 2MB)"}
        </span>
        <span className="text-[12px] font-OpenSansRegular text-subTitleColor mt-1">
          Your logo brands your Makani workspace, {vocabularize("work orders")}, and customer-facing documents.
        </span>
      </div>
    </>
  );

  const tradePicker = () => (
    <>
      <p className="text-[24px] font-OpenSansSemiBold text-titleColor">What's your trade?</p>
      <p className="text-[14px] font-OpenSansRegular text-subTitleColor leading-[22px] mt-[6px] mb-[16px]">
        This loads a starting preset — vocabulary, checklists, and compliance tools used by companies like
        yours. You can change every bit of it in the steps that follow.
      </p>

      <div className="grid grid-cols-3 gap-3">
        {Object.entries(TRADES).map(([key, preset]) => {
          const selected = trade === key;
          return (
            <div
              key={key}
              onClick={() => pickTrade(key)}
              className={`border-[1.5px] rounded-[12px] px-[14px] py-4 text-center cursor-pointer ${
                selected
                  ? "border-blueColor bg-blueColor/10"
                  : errors.trade
                    ? "border-red-500"
                    : "border-borderColor hover:border-blueColor"
              }`}
            >
              <p className="text-[26px] leading-none">{preset.icon}</p>
              <p className="text-[14px] font-OpenSansSemiBold text-titleColor mt-[6px]">{preset.name}</p>
              <p className="text-[11.5px] font-OpenSansRegular text-subTitleColor mt-[3px]">
                {preset.description}
              </p>
            </div>
          );
        })}

        {trade === "other" && (
          <div className="border-[1.5px] border-dashed border-blueColor bg-blueColor/10 rounded-[12px] px-[14px] py-4 flex flex-col justify-center">
            <p className="text-[12.5px] font-OpenSansSemiBold text-titleColor mb-[6px]">
              What's your trade called? *
            </p>
            {field(
              tradeOther,
              (e) => {
                setTradeOther(e.target.value);
                clearError("tradeOther");
              },
              "e.g. Pool Service, Pest Control",
              errors.tradeOther,
            )}
          </div>
        )}
      </div>
      {error(errors.trade)}
      {error(errors.tradeOther)}
    </>
  );

  const vocabulary = () => (
    <>
      <p className="text-[24px] font-OpenSansSemiBold text-titleColor">Speak your language</p>
      <p className="text-[14px] font-OpenSansRegular text-subTitleColor leading-[22px] mt-[6px] mb-[16px]">
        Makani renames itself around your company. Whatever your crew calls things in the yard at 6am — that's
        what the software should say.
      </p>

      <table className="w-full table-fixed">
        <thead>
          <tr className="border-b-[1px] border-borderColor">
            <th className="w-[190px] text-left px-[10px] py-2">
              <span className="text-[12px] font-OpenSansSemiBold text-subTitleColor uppercase tracking-[0.4px]">
                Makani concept
              </span>
            </th>
            <th className="text-left px-[10px] py-2">
              <span className="text-[12px] font-OpenSansSemiBold text-subTitleColor uppercase tracking-[0.4px]">
                What it means
              </span>
            </th>
            <th className="w-[240px] text-left px-[10px] py-2">
              <span className="text-[12px] font-OpenSansSemiBold text-subTitleColor uppercase tracking-[0.4px]">
                What YOUR company calls it
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {VOCAB_KEYS.map(([key, name, meaning]) => (
            <tr key={key} className="border-b-[1px] border-borderColor">
              <td className="px-[10px] py-2">
                <span className="inline-block text-[12px] font-OpenSansSemiBold text-blueColor bg-blueColor/10 rounded-full px-[10px] py-[3px]">
                  {name}
                </span>
              </td>
              <td className="px-[10px] py-2">
                <span className="text-[12.5px] font-OpenSansRegular text-subTitleColor">{meaning}</span>
              </td>
              <td className="px-[10px] py-2">
                {field(
                  vocab[key] || "",
                  (e) => {
                    setVocab((prev) => ({ ...prev, [key]: e.target.value }));
                    clearError(`vocab.${key}`);
                  },
                  name,
                  errors[`vocab.${key}`],
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[12px] font-OpenSansRegular text-subTitleColor mt-[10px]">
        Pre-filled from your trade preset — edit anything.
      </p>
    </>
  );

  const workSetup = () => (
    <>
      {isAddBranch && (
        <BranchAddModal
          branch={null}
          branches={branches}
          onAddClicked={branchAdded}
          onCancelClicked={() => setIsAddBranch(false)}
        />
      )}
      {editingBranch && (
        <BranchAddModal
          branch={editingBranch}
          branches={branches}
          onUpateClicked={branchUpdated}
          onCancelClicked={() => setEditingBranch(null)}
        />
      )}

      <p className="text-[24px] font-OpenSansSemiBold text-titleColor">How you work</p>
      <p className="text-[14px] font-OpenSansRegular text-subTitleColor leading-[22px] mt-[6px] mb-[16px]">
        The work you dispatch, and the branches you dispatch it from. Every {term("workOrder").toLowerCase()} is
        filed under one of each.
      </p>

      <p className="text-[15px] font-OpenSansSemiBold text-titleColor">Types of work you dispatch *</p>
      <p className="text-[12.5px] font-OpenSansRegular text-subTitleColor mt-[3px] mb-[10px]">
        Pre-ticked from your trade preset — untick what you don't do.
      </p>

      <div className="grid grid-cols-3 gap-2.5">
        {presetWork.map((name) => {
          const picked = selectedWork.some((item) => sameWork(item, name));
          return (
            <div
              key={name}
              onClick={() => toggleWork(name)}
              className={`flex items-center gap-2 border-[1.5px] rounded-[10px] px-[12px] py-[10px] cursor-pointer ${
                picked ? "border-blueColor bg-blueColor/10" : "border-borderColor hover:border-blueColor"
              }`}
            >
              <span
                className={`w-[16px] h-[16px] rounded-[4px] border-[1.5px] shrink-0 flex items-center justify-center text-[10px] text-white ${
                  picked ? "bg-blueColor border-blueColor" : "border-borderColor"
                }`}
              >
                {picked ? "✓" : ""}
              </span>
              <span className="text-[13px] font-OpenSansRegular text-titleColor">{name}</span>
            </div>
          );
        })}

        {customWork.map((name) => (
          <div
            key={name}
            className="flex items-center gap-2 border-[1.5px] border-blueColor bg-blueColor/10 rounded-[10px] px-[12px] py-[10px]"
          >
            <span className="w-[16px] h-[16px] rounded-[4px] bg-blueColor border-[1.5px] border-blueColor shrink-0 flex items-center justify-center text-[10px] text-white">
              ✓
            </span>
            <span className="text-[13px] font-OpenSansRegular text-titleColor flex-1 truncate">{name}</span>
            <span
              onClick={() => toggleWork(name)}
              className="text-[13px] text-subTitleColor cursor-pointer hover:text-red-500"
            >
              ✕
            </span>
          </div>
        ))}
      </div>
      {error(errors.work)}

      <p className="text-[13px] font-OpenSansSemiBold text-titleColor mb-[6px] mt-[16px]">Other types of work</p>
      <div className="flex gap-2">
        <div className="flex-1">
          {field(
            otherWork,
            (e) => setOtherWork(e.target.value),
            "e.g. Warranty Call, Retrofit",
            false,
            "text",
            undefined,
            (e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addOtherWork();
              }
            },
          )}
        </div>
        <button
          onClick={addOtherWork}
          disabled={!otherWork.trim()}
          className={`text-[13px] font-OpenSansSemiBold text-titleColor border-[1px] border-borderColor rounded-[8px] px-5 h-[38px] ${
            otherWork.trim() ? "cursor-pointer hover:border-blueColor" : "opacity-40 cursor-not-allowed"
          }`}
        >
          Add
        </button>
      </div>

      <div className="flex items-end justify-between mt-[26px] mb-[10px]">
        <div>
          <p className="text-[15px] font-OpenSansSemiBold text-titleColor">Branches *</p>
          <p className="text-[12.5px] font-OpenSansRegular text-subTitleColor mt-[3px]">
            Each branch has its own {term("workOrder").toLowerCase()} numbering, timecard cycle and timezone. Add
            one per location you dispatch from.
          </p>
        </div>
        {can("branch:create") && (
          <button
            onClick={() => setIsAddBranch(true)}
            className="text-[13px] font-OpenSansSemiBold text-white bg-blueColor rounded-[8px] px-4 h-[34px] shrink-0 cursor-pointer"
          >
            + Branch
          </button>
        )}
      </div>

      {branches.length === 0 ? (
        <div className="w-full border-dashed border-[2px] border-borderColor rounded-[12px] py-[26px] flex flex-col items-center">
          <span className="text-[13px] font-OpenSansRegular text-subTitleColor">
            No branches yet — add the one you dispatch from.
          </span>
        </div>
      ) : (
        <BranchTable
          branches={branches}
          onDeleteBranch={branchDeleted}
          onEditClicked={(index) => setEditingBranch(branches[index])}
          canEdit={can("branch:update")}
          canDelete={can("branch:delete")}
        />
      )}
      {error(errors.branch)}
    </>
  );

  const teamRoles = () => (
    <>
      <p className="text-[24px] font-OpenSansSemiBold text-titleColor">Who's on your team</p>
      <p className="text-[14px] font-OpenSansRegular text-subTitleColor leading-[22px] mt-[6px] mb-[16px]">
        The roles you hand out when you invite people. You decide what each one can do later — here we just
        create them.
      </p>

      <p className="text-[15px] font-OpenSansSemiBold text-titleColor">Roles you'll use</p>
      <p className="text-[12.5px] font-OpenSansRegular text-subTitleColor mt-[3px] mb-[10px]">
        Pre-ticked from your trade preset — untick the ones you don't need.
      </p>

      <div className="grid grid-cols-2 gap-2.5">
        <div className="flex items-start gap-2 border-[1.5px] border-borderColor bg-gray-50 rounded-[10px] px-[12px] py-[10px]">
          <span className="w-[16px] h-[16px] rounded-[4px] bg-gray-400 border-[1.5px] border-gray-400 shrink-0 mt-[2px] flex items-center justify-center text-[10px] text-white">
            ✓
          </span>
          <div>
            <p className="text-[13px] font-OpenSansSemiBold text-titleColor">Administrator</p>
            <p className="text-[11.5px] font-OpenSansRegular text-subTitleColor">
              Full access — that's you. Always here.
            </p>
          </div>
        </div>

        {rolePresets.map((preset) => {
          const picked = selectedRoles.some((name) => sameWork(name, preset.name));
          return (
            <div
              key={preset.key}
              onClick={() => toggleRole(preset.name)}
              className={`flex items-start gap-2 border-[1.5px] rounded-[10px] px-[12px] py-[10px] cursor-pointer ${
                picked ? "border-blueColor bg-blueColor/10" : "border-borderColor hover:border-blueColor"
              }`}
            >
              <span
                className={`w-[16px] h-[16px] rounded-[4px] border-[1.5px] shrink-0 mt-[2px] flex items-center justify-center text-[10px] text-white ${
                  picked ? "bg-blueColor border-blueColor" : "border-borderColor"
                }`}
              >
                {picked ? "✓" : ""}
              </span>
              <div>
                <p className="text-[13px] font-OpenSansSemiBold text-titleColor">{preset.name}</p>
                <p className="text-[11.5px] font-OpenSansRegular text-subTitleColor">{preset.description}</p>
              </div>
            </div>
          );
        })}

        {customRoles.map((name) => (
          <div
            key={name}
            className="flex items-start gap-2 border-[1.5px] border-blueColor bg-blueColor/10 rounded-[10px] px-[12px] py-[10px]"
          >
            <span className="w-[16px] h-[16px] rounded-[4px] bg-blueColor border-[1.5px] border-blueColor shrink-0 mt-[2px] flex items-center justify-center text-[10px] text-white">
              ✓
            </span>
            <p className="text-[13px] font-OpenSansSemiBold text-titleColor flex-1 truncate">{name}</p>
            <span
              onClick={() => toggleRole(name)}
              className="text-[13px] text-subTitleColor cursor-pointer hover:text-red-500"
            >
              ✕
            </span>
          </div>
        ))}
      </div>

      <p className="text-[13px] font-OpenSansSemiBold text-titleColor mb-[6px] mt-[16px]">Other roles</p>
      <div className="flex gap-2">
        <div className="flex-1">
          {field(
            otherRole,
            (e) => setOtherRole(e.target.value),
            "e.g. Apprentice, Estimator, Safety Officer",
            false,
            "text",
            undefined,
            (e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addOtherRole();
              }
            },
          )}
        </div>
        <button
          onClick={addOtherRole}
          disabled={!otherRole.trim()}
          className={`text-[13px] font-OpenSansSemiBold text-titleColor border-[1px] border-borderColor rounded-[8px] px-5 h-[38px] ${
            otherRole.trim() ? "cursor-pointer hover:border-blueColor" : "opacity-40 cursor-not-allowed"
          }`}
        >
          Add
        </button>
      </div>
    </>
  );

  // A read-only recap of what the wizard wrote. Each block links back to the step
  // that owns it, so a correction is made where it was made the first time —
  // there is nothing to edit here.
  const summary = (title, target, body) => (
    <div className="border-[1px] border-borderColor rounded-[12px] p-4 mb-3">
      <div className="flex items-center justify-between mb-[10px]">
        <p className="text-[14px] font-OpenSansSemiBold text-titleColor">{title}</p>
        <span
          onClick={() => goTo(target)}
          className="text-[12px] font-OpenSansSemiBold text-blueColor cursor-pointer hover:underline"
        >
          Edit
        </span>
      </div>
      {body}
    </div>
  );

  const summaryRow = (name, value) => (
    <div key={name} className="flex gap-3 py-[3px]">
      <span className="w-[130px] shrink-0 text-[12.5px] font-OpenSansRegular text-subTitleColor">{name}</span>
      <span className="text-[12.5px] font-OpenSansSemiBold text-titleColor break-all">{value || "—"}</span>
    </div>
  );

  const chips = (values) => (
    <div className="flex flex-wrap gap-[6px]">
      {values.length === 0 ? (
        <span className="text-[12.5px] font-OpenSansRegular text-subTitleColor">—</span>
      ) : (
        values.map((value) => (
          <span
            key={value}
            className="text-[12px] font-OpenSansSemiBold text-blueColor bg-blueColor/10 rounded-full px-[10px] py-[4px]"
          >
            {value}
          </span>
        ))
      )}
    </div>
  );

  const reviewSubmit = () => (
    <>
      <p className="text-[24px] font-OpenSansSemiBold text-titleColor">Your Makani blueprint</p>
      <p className="text-[14px] font-OpenSansRegular text-subTitleColor leading-[22px] mt-[6px] mb-[16px]">
        Here's what we've set up. Everything below is already saved and stays editable in Settings — submitting
        just closes the setup and takes you into Makani.
      </p>

      {summary(
        "Company",
        0,
        <div className="flex gap-4">
          {logoPreview && (
            <img
              src={logoPreview}
              alt="Company logo"
              className="h-[54px] w-[54px] object-contain border-[1px] border-borderColor rounded-[8px] p-1"
            />
          )}
          <div className="flex-1">
            {summaryRow("Name", companyName)}
            {summaryRow("Primary contact", contactName)}
            {summaryRow("Email", email)}
            {summaryRow("Phone", phone)}
            {summaryRow("Website", website)}
          </div>
        </div>,
      )}

      {summary(
        "Trade",
        1,
        <div className="flex items-center gap-2">
          <span className="text-[20px]">{TRADES[trade]?.icon}</span>
          <span className="text-[13px] font-OpenSansSemiBold text-titleColor">
            {trade === "other" ? tradeOther : TRADES[trade]?.name}
          </span>
        </div>,
      )}

      {summary(
        "Your words",
        2,
        <div className="grid grid-cols-2 gap-x-4">
          {VOCAB_KEYS.map(([key, name]) => summaryRow(name, vocab[key]))}
        </div>,
      )}

      {summary(
        "Work setup",
        3,
        <>
          <p className="text-[12.5px] font-OpenSansRegular text-subTitleColor mb-[6px]">
            Types of work you dispatch
          </p>
          {chips(selectedWork)}
          <p className="text-[12.5px] font-OpenSansRegular text-subTitleColor mt-[10px] mb-[6px]">Branches</p>
          {chips(branches.map((branch) => branch.name))}
          {branches.length > 0 && (
            <p className="text-[11.5px] font-OpenSansRegular text-subTitleColor mt-[8px]">
              You'll be added to {branches.length === 1 ? "this branch" : "these branches"} when you finish setup.
            </p>
          )}
        </>,
      )}

      {summary("Roles", 4, chips(["Administrator", ...selectedRoles]))}

      {summary(
        "Your data",
        5,
        dataSummary ? (
          <div className="grid grid-cols-2 gap-x-4">
            {summaryRow("Customers created", String(dataSummary.customers || 0))}
            {summaryRow("Locations", String(dataSummary.locations || 0))}
            {summaryRow("Invitations sent", String(dataSummary.invites || 0))}
            {summaryRow("Vendors", String(dataSummary.vendors || 0))}
          </div>
        ) : (
          <p className="text-[12.5px] font-OpenSansRegular text-subTitleColor">
            Nothing imported — you can bring in customers, vendors and your team anytime.
          </p>
        ),
      )}
    </>
  );

  const comingSoon = () => (
    <div className="h-full flex flex-col items-center justify-center text-center">
      <p className="text-[18px] font-OpenSansSemiBold text-titleColor">{STEPS[step].title}</p>
      <p className="text-[14px] font-OpenSansRegular text-subTitleColor mt-2">This step is coming next.</p>
    </div>
  );

  const currentView = () => {
    if (step === 0) return companyProfile();
    if (step === 1) return tradePicker();
    if (step === 2) return vocabulary();
    if (step === 3) return workSetup();
    if (step === 4) return teamRoles();
    if (step === 5)
      return (
        <YourData
          branches={branches}
          roles={roles}
          onImported={dataImported}
          onBusyChange={setDataBusy}
          customers={customerImport}
          setCustomers={setCustomerImport}
          invites={inviteImport}
          setInvites={setInviteImport}
          vendors={vendorImport}
          setVendors={setVendorImport}
          inviteRoleId={inviteRoleId}
          setInviteRoleId={setInviteRoleId}
        />
      );
    if (isLast) return reviewSubmit();
    return comingSoon();
  };

  return (
    <div className="w-screen h-screen bg-backgroundColor flex flex-col overflow-auto">
      <div className="w-full bg-white border-b-[1px] border-borderColor px-8 py-4 flex items-center justify-between">
        <div>
          <p className="text-[22px] font-OpenSansBold tracking-[4px] text-titleColor">MAKANI</p>
          <p className="text-[9px] font-OpenSansSemiBold tracking-[1.5px] text-subTitleColor">
            SERVICE CONTRACTING MADE BETTER
          </p>
        </div>
        <span className="text-[13px] font-OpenSansRegular text-subTitleColor">New Customer Onboarding</span>
      </div>

      <div className="flex-1 w-full max-w-[1200px] mx-auto flex gap-7 px-6 py-7">
        <StepRail current={step} onJump={goTo} />

        <div className="flex-1 bg-white border-[1px] border-borderColor rounded-[14px] p-8 flex flex-col min-h-[540px]">
          <div className="flex-1">{isLoading ? null : currentView()}</div>

          <div className="flex items-center justify-between border-t-[1px] border-borderColor pt-5 mt-6">
            <button
              onClick={() => goTo(step - 1)}
              className={`text-[14px] font-OpenSansSemiBold text-titleColor border-[1px] border-borderColor rounded-[9px] px-6 py-[10px] ${
                step === 0 ? "invisible" : "cursor-pointer"
              }`}
            >
              Back
            </button>
            <span className="text-[12px] font-OpenSansRegular text-subTitleColor">
              Step {step + 1} of {STEPS.length}
            </span>
            <button
              onClick={continueClicked}
              disabled={isSaving || !canContinue}
              className={`text-[14px] font-OpenSansSemiBold text-white bg-blueColor rounded-[9px] px-6 py-[10px] min-w-[140px] flex items-center justify-center ${
                isSaving || !canContinue ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              {isSaving ? (
                <Loader variant="button" ariaLabel="saving-onboarding-step" />
              ) : isLast ? (
                "Finish setup"
              ) : (
                "Continue"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
